// Permit summary card shared by Home and the permit list. Shows only what the server
// returned; the eligibility line is the server's status in words.
import { Link } from 'react-router-dom'
import { Badge, cx } from '@/components/ui'
import type { PermitSummary } from '@/domain/types'
import { fmtDateTime } from '@/lib/format'
import { cycleStateInfo, eligibilityInfo, omPendingInfo, pendingActionLabel, permitStateInfo, roleLabel, type Tone, waitingActions } from './permitList'

const TONE_TEXT: Record<Tone, string> = {
  ok: 'text-ok',
  bad: 'text-bad',
  warn: 'text-warn',
  info: 'text-info',
  neutral: 'text-ink-soft',
}

function StatusIcon({ kind }: { kind: 'ok' | 'stop' | 'warn' }) {
  return (
    <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {kind === 'ok' ? <path d="M20 6L9 17l-5-5" /> : null}
      {kind === 'stop' ? (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M8.5 8.5l7 7M15.5 8.5l-7 7" />
        </>
      ) : null}
      {kind === 'warn' ? (
        <>
          <path d="M12 3l9.5 17h-19z" />
          <path d="M12 10v4M12 17.5v.01" />
        </>
      ) : null}
    </svg>
  )
}

/**
 * The server's work-eligibility status as an icon plus words. Colour follows the status
 * on live (authorised) permits; elsewhere the same words are shown in a neutral colour.
 */
export function EligibilityLine({ status, emphasise }: { status: string; emphasise: boolean }) {
  const info = eligibilityInfo(status)
  const kind = info.mayProceed ? 'ok' : info.tone === 'warn' ? 'warn' : 'stop'
  return (
    <p className={cx('flex items-start gap-1.5 text-sm font-semibold', emphasise ? TONE_TEXT[info.tone] : 'text-ink-soft')}>
      <StatusIcon kind={kind} />
      <span>{info.label}</span>
    </p>
  )
}

/** `compact` drops the to-do box and the footer (used where the to-dos are listed already). */
export function PermitCard({ p, compact }: { p: PermitSummary; compact?: boolean }) {
  const st = permitStateInfo(p.lifecycle_state)
  const cycle = p.current_cycle
  const cs = cycle ? cycleStateInfo(cycle.cycle_state) : null
  const classA = p.permit_class === 'A'
  const todo = waitingActions(p)
  const om = omPendingInfo(p)
  return (
    <Link
      to={`/permits/${p.id}`}
      className={cx(
        'block rounded-xl border border-l-8 border-line bg-card p-3 shadow-sm transition hover:shadow-md',
        classA ? 'border-l-class-a' : 'border-l-class-b',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="code font-semibold text-ink">{p.permit_number}</span>
        <span className={cx('text-xs font-bold uppercase tracking-wide', classA ? 'text-class-a' : 'text-class-b')}>Class {p.permit_class}</span>
        <Badge tone={st.tone}>{st.label}</Badge>
        {om ? <Badge tone={om.tone}>{om.label}</Badge> : null}
        {p.is_synthetic ? <Badge tone="synthetic">Synthetic</Badge> : null}
      </div>
      <p className="mt-1 font-semibold text-ink">{p.title || 'Untitled draft'}</p>
      <p className="text-sm text-ink-soft">
        {p.site_area.name} - {p.site.name}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <span className="text-ink-soft">Daily cycle:</span>
        {cycle && cs ? (
          <>
            <Badge tone={cs.tone}>
              #{cycle.cycle_number} {cs.label}
            </Badge>
            {cycle.validation_to ? <span className="text-xs text-ink-soft">validation ends {fmtDateTime(cycle.validation_to)}</span> : null}
          </>
        ) : (
          <span className="text-ink">None yet</span>
        )}
      </div>
      <div className="mt-1">
        <EligibilityLine status={p.eligibility_status} emphasise={p.lifecycle_state === 'authorised'} />
      </div>
      {!compact && todo.length > 0 ? (
        <div className="mt-2 rounded-lg border border-brand/30 bg-brand-light px-2 py-1.5 text-sm text-ink">
          <span className="font-semibold text-brand-dark">Waiting for you: </span>
          {todo.map(pendingActionLabel).join('; ')}
        </div>
      ) : null}
      {compact ? null : (
        <div className="mt-2 flex flex-wrap justify-between gap-x-3 text-xs">
          {p.my_roles.length > 0 ? <span className="text-ink-soft">Your role: {p.my_roles.map(roleLabel).join(', ')}</span> : <span />}
          <span className="text-ink-faint">Updated {fmtDateTime(p.updated_at)}</span>
        </div>
      )}
    </Link>
  )
}
